import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { Calendar, Users, TrendingUp, UserCheck, AlertTriangle, Download, FileText } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarPicker } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { format } from 'date-fns';
import { toast } from 'sonner';

const COLORS = ['#1E40AF', '#F59E0B', '#14B8A6', '#94A3B8'];

function PDFDownload() {
  const [pdfDate, setPdfDate] = useState(new Date());
  const [calOpen, setCalOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const handleDownload = async () => {
    setDownloading(true);
    try {
      const dateStr = format(pdfDate, 'yyyy-MM-dd');
      const res = await api.get(`/reports/pdf?report_date=${dateStr}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `PastorOS_Report_${dateStr}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { console.error('PDF download failed:', err); }
    finally { setDownloading(false); }
  };
  return (
    <div className="flex items-center gap-2">
      <Popover open={calOpen} onOpenChange={setCalOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="border-slate-300 text-sm">
            <Calendar className="h-3.5 w-3.5 mr-1.5" />{format(pdfDate, 'MMM d, yyyy')}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          <CalendarPicker mode="single" selected={pdfDate} onSelect={d => { if (d) setPdfDate(d); setCalOpen(false); }} />
        </PopoverContent>
      </Popover>
      <Button data-testid="download-pdf" size="sm" onClick={handleDownload} disabled={downloading} className="bg-blue-700 hover:bg-blue-800">
        <Download className="h-3.5 w-3.5 mr-1.5" />{downloading ? 'Generating...' : 'Download PDF'}
      </Button>
    </div>
  );
}

const REPORT_TYPES = [
  { value: 'attendance_detail', label: 'Attendance Detail', desc: 'Who attended which services in a date range' },
  { value: 'attendance_comparison', label: 'Attendance Comparison', desc: 'Weekly attendance totals side by side' },
  { value: 'people_by_type', label: 'People by Type', desc: 'List of people filtered by attendee type' },
  { value: 'ministry_roster', label: 'Ministry Roster', desc: 'People in a specific ministry with contact info' },
  { value: 'follow_up_log', label: 'Follow-Up Log', desc: 'All touchpoint actions in a date range' },
  { value: 'birthday_anniversary', label: 'Birthdays & Anniversaries', desc: 'Upcoming celebrations in a date range' },
];

function CustomReportBuilder() {
  const [reportType, setReportType] = useState('attendance_detail');
  const [startDate, setStartDate] = useState(format(new Date(Date.now() - 30*24*60*60*1000), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [personTypes, setPersonTypes] = useState([]);
  const [results, setResults] = useState(null);
  const [running, setRunning] = useState(false);
  const [ministries, setMinistries] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedMinistry, setSelectedMinistry] = useState('');
  const [selectedEvent, setSelectedEvent] = useState('');

  useEffect(() => {
    Promise.all([api.get('/ministries'), api.get('/events')]).then(([m, e]) => { setMinistries(m.data); setEvents(e.data); });
  }, []);

  const runReport = async () => {
    setRunning(true);
    try {
      const res = await api.post('/reports/custom', {
        start_date: startDate, end_date: endDate, report_type: reportType,
        person_types: personTypes, event_id: selectedEvent, ministry: selectedMinistry,
      });
      setResults(res.data);
    } catch { toast.error('Failed to generate report'); }
    finally { setRunning(false); }
  };

  const exportCSV = () => {
    if (!results?.data?.length) return;
    const data = results.data;
    const keys = Object.keys(data[0]).filter(k => !k.startsWith('_') && k !== 'person_id');
    const header = keys.join(',');
    const rows = data.map(r => keys.map(k => { const v = r[k]; return Array.isArray(v) ? `"${v.join(', ')}"` : `"${(v || '').toString().replace(/"/g, '""')}"` }).join(','));
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `PastorOS_${reportType}_${startDate}_to_${endDate}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const toggleType = (t) => setPersonTypes(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);
  const selectedReport = REPORT_TYPES.find(r => r.value === reportType);

  return (
    <div className="space-y-4">
      <Card className="border-slate-200 shadow-sm">
        <CardHeader><CardTitle className="text-lg font-['Outfit']">Custom Report Builder</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2 block">Report Type</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {REPORT_TYPES.map(rt => (
                <label key={rt.value} className={`p-3 rounded-lg border cursor-pointer transition-colors ${reportType === rt.value ? 'bg-blue-50 border-blue-300' : 'bg-white border-slate-200 hover:bg-slate-50'}`} onClick={() => setReportType(rt.value)}>
                  <p className={`text-sm font-medium ${reportType === rt.value ? 'text-blue-800' : 'text-slate-900'}`}>{rt.label}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{rt.desc}</p>
                </label>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div><Label>Start Date</Label><Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="mt-1 border-slate-300" /></div>
            <div><Label>End Date</Label><Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="mt-1 border-slate-300" /></div>
            {(reportType === 'attendance_detail' || reportType === 'attendance_comparison') && (
              <div><Label>Event</Label><Select value={selectedEvent || 'all'} onValueChange={v => setSelectedEvent(v === 'all' ? '' : v)}><SelectTrigger className="mt-1 border-slate-300"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All Events</SelectItem>{events.map(e => <SelectItem key={e.event_id} value={e.event_id}>{e.name}</SelectItem>)}</SelectContent></Select></div>
            )}
            {(reportType === 'ministry_roster' || reportType === 'people_by_type') && (
              <div><Label>Ministry</Label><Select value={selectedMinistry || 'all'} onValueChange={v => setSelectedMinistry(v === 'all' ? '' : v)}><SelectTrigger className="mt-1 border-slate-300"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All Ministries</SelectItem>{ministries.map(m => <SelectItem key={m.ministry_id} value={m.name}>{m.name}</SelectItem>)}</SelectContent></Select></div>
            )}
          </div>
          <div>
            <Label className="mb-1.5 block">Filter by Type (optional)</Label>
            <div className="flex gap-2 flex-wrap">
              {['regular', 'visitor', 'occasional', 'inactive'].map(t => (
                <label key={t} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border cursor-pointer text-sm capitalize ${personTypes.includes(t) ? 'bg-blue-50 border-blue-300 text-blue-800' : 'bg-white border-slate-200 text-slate-600'}`}>
                  <Checkbox checked={personTypes.includes(t)} onCheckedChange={() => toggleType(t)} className="h-3.5 w-3.5" />{t}
                </label>
              ))}
            </div>
          </div>
          <Button onClick={runReport} disabled={running} className="bg-blue-700 hover:bg-blue-800">
            {running ? 'Generating...' : `Run ${selectedReport?.label || 'Report'}`}
          </Button>
        </CardContent>
      </Card>
      {results && (
        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-['Outfit']">{selectedReport?.label} Results</CardTitle>
              <div className="flex items-center gap-2">
                <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">{results.count} records</Badge>
                {results.data?.length > 0 && <Button size="sm" variant="outline" onClick={exportCSV}><Download className="h-3.5 w-3.5 mr-1" />Export CSV</Button>}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {results.data?.length === 0 ? <p className="text-sm text-slate-500 py-4">No data found for the selected criteria.</p> : (
              <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {Object.keys(results.data[0] || {}).filter(k => !k.startsWith('_') && k !== 'person_id' && k !== 'followup_id').map(k => (
                        <TableHead key={k} className="text-xs font-bold uppercase tracking-widest text-slate-500 whitespace-nowrap">{k.replace(/_/g, ' ')}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {results.data.slice(0, 100).map((row, i) => (
                      <TableRow key={`${row.person_id || row.date || ''}-${i}`} className="border-slate-100">
                        {Object.entries(row).filter(([k]) => !k.startsWith('_') && k !== 'person_id' && k !== 'followup_id').map(([k, v]) => (
                          <TableCell key={k} className="text-sm whitespace-nowrap">{Array.isArray(v) ? v.join(', ') : k === 'present' ? (v ? 'Yes' : 'No') : String(v || '—')}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {results.data.length > 100 && <p className="text-xs text-slate-500 mt-2 text-center">Showing first 100 of {results.count} records. Export CSV for full data.</p>}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default function Reports() {
  const [trends, setTrends] = useState([]);
  const [trendEvents, setTrendEvents] = useState([]);
  const [absent, setAbsent] = useState([]);
  const [demographics, setDemographics] = useState(null);
  const [birthdays, setBirthdays] = useState([]);
  const [people, setPeople] = useState([]);
  const [selectedPerson, setSelectedPerson] = useState('');
  const [personAttendance, setPersonAttendance] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/reports/attendance-trends?weeks=12'),
      api.get('/reports/absent-people?weeks=3'),
      api.get('/reports/demographics'),
      api.get('/reports/birthdays?days=60'),
      api.get('/people'),
    ]).then(([t, a, d, b, p]) => {
      const tData = t.data.trends || t.data;
      setTrends((Array.isArray(tData) ? tData : []).map(d => ({ ...d, date: new Date(d.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) })));
      setTrendEvents(t.data.events || []);
      setAbsent(a.data);
      setDemographics(d.data);
      setBirthdays(b.data);
      setPeople(p.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedPerson) return;
    api.get(`/attendance/person/${selectedPerson}`).then(res => {
      setPersonAttendance(res.data.slice(0, 12).reverse().map(a => ({
        date: new Date(a.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        present: a.present ? 1 : 0,
      })));
    }).catch(console.error);
  }, [selectedPerson]);

  if (loading) return <div className="flex justify-center py-20"><div className="h-8 w-8 border-3 border-blue-700 border-t-transparent rounded-full animate-spin" /></div>;

  const pieData = demographics ? [
    { name: 'Regulars', value: demographics.regulars },
    { name: 'Visitors', value: demographics.visitors },
    { name: 'Occasional', value: demographics.occasional || 0 },
    { name: 'Inactive', value: demographics.inactive || 0 },
  ].filter(d => d.value > 0) : [];

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold font-['Outfit'] text-slate-900">Reports</h2>
        <PDFDownload />
      </div>
      <Tabs defaultValue="trends">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="trends" data-testid="report-tab-trends"><TrendingUp className="h-3 w-3 mr-1" />Trends</TabsTrigger>
          <TabsTrigger value="individual" data-testid="report-tab-individual"><Users className="h-3 w-3 mr-1" />Individual</TabsTrigger>
          <TabsTrigger value="absent" data-testid="report-tab-absent"><AlertTriangle className="h-3 w-3 mr-1" />Absentees</TabsTrigger>
          <TabsTrigger value="demographics" data-testid="report-tab-demographics"><UserCheck className="h-3 w-3 mr-1" />Demographics</TabsTrigger>
          <TabsTrigger value="birthdays" data-testid="report-tab-birthdays"><Calendar className="h-3 w-3 mr-1" />Birthdays</TabsTrigger>
          <TabsTrigger value="custom" data-testid="report-tab-custom"><FileText className="h-3 w-3 mr-1" />Custom Report</TabsTrigger>
        </TabsList>

        <TabsContent value="trends" className="mt-4">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader><CardTitle className="text-lg font-['Outfit']">Weekly Attendance Trends</CardTitle></CardHeader>
            <CardContent>
              <div className="h-80" data-testid="trends-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={trends} barGap={-20}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#64748b' }} />
                    <YAxis tick={{ fontSize: 11, fill: '#64748b' }} />
                    <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                    <Bar dataKey="total_tracked" fill="#CBD5E1" radius={[4, 4, 0, 0]} name="Total Tracked" barSize={40} />
                    {trendEvents.map((evt, i) => {
                      const colors = ['#1E40AF', '#DC2626', '#059669', '#7C3AED', '#D97706', '#0891B2'];
                      return <Bar key={evt.event_id} dataKey={`present_${evt.event_id}`} fill={colors[i % colors.length]} radius={[4, 4, 0, 0]} name={evt.name} barSize={30} />;
                    })}
                    <ReferenceLine y={12} stroke="#F97316" strokeWidth={2} strokeDasharray="6 3" label={{ value: 'Goal: 12', position: 'right', fill: '#F97316', fontSize: 12 }} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="individual" className="mt-4">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-['Outfit']">Individual Attendance History</CardTitle>
              <Select value={selectedPerson} onValueChange={setSelectedPerson}>
                <SelectTrigger data-testid="person-select" className="w-64 mt-2 border-slate-300"><SelectValue placeholder="Select a person" /></SelectTrigger>
                <SelectContent>
                  {people.map(p => <SelectItem key={p.person_id} value={p.person_id}>{p.first_name} {p.last_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </CardHeader>
            <CardContent>
              {selectedPerson && personAttendance.length > 0 ? (
                <div className="h-64" data-testid="individual-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={personAttendance}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#64748b' }} />
                      <YAxis tick={{ fontSize: 11, fill: '#64748b' }} domain={[0, 1]} ticks={[0, 1]} tickFormatter={v => v ? 'Yes' : 'No'} />
                      <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} formatter={v => v ? 'Present' : 'Absent'} />
                      <Line type="stepAfter" dataKey="present" stroke="#1E40AF" strokeWidth={2} dot={{ fill: '#1E40AF', r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : <p className="text-sm text-slate-500 py-8 text-center">Select a person to view their attendance history</p>}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="absent" className="mt-4">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader><CardTitle className="text-lg font-['Outfit']">People Absent 3+ Weeks</CardTitle></CardHeader>
            <CardContent>
              {absent.length === 0 ? <p className="text-sm text-slate-500 py-4">Everyone has attended recently!</p> : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="absent-report-list">
                  {absent.map(a => (
                    <div key={a.person_id} className="flex items-center justify-between px-4 py-3 rounded-lg bg-slate-50">
                      <span className="text-sm font-medium text-slate-700">{a.first_name} {a.last_name}</span>
                      <Badge variant="outline" className={`text-xs ${a.person_type === 'regular' ? 'border-blue-200 text-blue-700' : 'border-amber-200 text-amber-700'}`}>{a.person_type}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="demographics" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-slate-200 shadow-sm">
              <CardHeader><CardTitle className="text-lg font-['Outfit']">Visitor vs Regular</CardTitle></CardHeader>
              <CardContent>
                <div className="h-64" data-testid="demographics-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={90} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                        {pieData.map((entry, i) => <Cell key={entry.name} fill={COLORS[i]} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200 shadow-sm">
              <CardHeader><CardTitle className="text-lg font-['Outfit']">Summary</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center p-3 rounded-lg bg-slate-50">
                  <span className="text-sm text-slate-600">Total People</span>
                  <span className="text-lg font-bold text-slate-900">{demographics?.total}</span>
                </div>
                <div className="flex justify-between items-center p-3 rounded-lg bg-blue-50">
                  <span className="text-sm text-blue-700">Regulars</span>
                  <span className="text-lg font-bold text-blue-900">{demographics?.regulars}</span>
                </div>
                <div className="flex justify-between items-center p-3 rounded-lg bg-amber-50">
                  <span className="text-sm text-amber-700">Visitors</span>
                  <span className="text-lg font-bold text-amber-900">{demographics?.visitors}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="birthdays" className="mt-4">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader><CardTitle className="text-lg font-['Outfit']">Upcoming Birthdays (60 days)</CardTitle></CardHeader>
            <CardContent>
              {birthdays.length === 0 ? <p className="text-sm text-slate-500 py-4">No upcoming birthdays</p> : (
                <div className="space-y-2" data-testid="birthday-report-list">
                  {birthdays.map(b => (
                    <div key={b.person_id} className="flex items-center justify-between px-4 py-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-full bg-blue-100 flex items-center justify-center text-sm font-medium text-blue-800">{b.first_name[0]}{b.last_name[0]}</div>
                        <div>
                          <p className="text-sm font-medium text-slate-900">{b.first_name} {b.last_name}</p>
                          <p className="text-xs text-slate-500">{new Date(b.birthday + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}</p>
                        </div>
                      </div>
                      <Badge variant="outline" className={`text-xs ${b.days_until <= 7 ? 'border-emerald-200 text-emerald-700 bg-emerald-50' : 'border-slate-200 text-slate-600'}`}>
                        {b.days_until === 0 ? 'Today!' : `in ${b.days_until} days`}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="custom" className="mt-4">
          <CustomReportBuilder />
        </TabsContent>
      </Tabs>
    </div>
  );
}
